import React, { useState, useEffect, useRef } from 'react';
import { FaInfoCircle, FaPlus, FaDownload } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import FinancePageHeader from '../components/finance/FinancePageHeader';
import { useFinanceEmbedded } from '../hooks/useFinanceEmbedded';
import AddButton from '../components/AddButton';
import SaveButton from '../components/SaveButton';
import EditButton from '../components/EditButton';
import { useNavigate } from 'react-router-dom';
import { 
  loadLaborBurdenData, 
  saveLaborBurdenData, 
  generateFromOrgChart,
  addLaborBurdenEmployee,
  updateLaborBurdenEmployee,
  removeLaborBurdenEmployee
} from '../services/laborBurdenService';
import PortalBackground from '../components/PortalBackground';

// overrideUserId lets an internal viewer (CRM Portal View) load/save a specific
// client's labor burden data instead of their own.
const LaborBurdenCalculator = ({ embedded: embeddedProp, overrideUserId = null }) => {
  const embedded = useFinanceEmbedded(embeddedProp);
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  // The uid whose labor burden data is read/written.
  const effectiveUid = overrideUserId || currentUser?.uid;

  // Guard so simply viewing a client (override) doesn't overwrite their data:
  // autosave only fires after the initial load has completed. Flipped true by a
  // dedicated effect that runs after the autosave effects have skipped the
  // load-triggered save.
  const didLoadRef = useRef(false);

  // Loading and saving states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // My Company tab state
  const [employees, setEmployees] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    type: 'hourly',
    name: '',
    jobTitle: '',
    rate: 0
  });

  // Add state for editing employee
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Summary statistics for My Company tab
  const [summaryStats, setSummaryStats] = useState({
    hourly: 0,
    daily: 0,
    weekly: 0,
    monthly: 0,
    annual: 0
  });

  // Global inputs
  const [workDays, setWorkDays] = useState(251);
  const [pto, setPto] = useState(10);
  const [holidays, setHolidays] = useState(7);

  // Hourly employee inputs
  const [hourlyRate, setHourlyRate] = useState(30);
  const [hourlyExtraCosts, setHourlyExtraCosts] = useState(15);
  const [unbillablePercentage, setUnbillablePercentage] = useState(10);

  // Salaried employee inputs
  const [salaryType, setSalaryType] = useState('annual'); // 'weekly', 'monthly', 'annual'
  const [salaryRate, setSalaryRate] = useState(62400); // Default annual ($30/hr equivalent)
  const [salaryExtraCosts, setSalaryExtraCosts] = useState(15);

  // Calculated values for hourly employee
  const [hourlyBurden, setHourlyBurden] = useState(0);
  const [extraCostsAmount, setExtraCostsAmount] = useState(0);
  const [unbillableAmount, setUnbillableAmount] = useState(0);
  const [daysOffAmount, setDaysOffAmount] = useState(0);
  const [holidaysAmount, setHolidaysAmount] = useState(0);
  const [totalHourlyBurden, setTotalHourlyBurden] = useState(0);
  const [hourlyDayRate, setHourlyDayRate] = useState(0);
  const [hourlyWeekRate, setHourlyWeekRate] = useState(0);
  const [hourlyMonthRate, setHourlyMonthRate] = useState(0);
  const [hourlyAnnualRate, setHourlyAnnualRate] = useState(0);

  // Calculated values for salaried employee
  const [weeklyRate, setWeeklyRate] = useState(0);
  const [monthlyRate, setMonthlyRate] = useState(0);
  const [annualRate, setAnnualRate] = useState(0);
  const [salaryExtraCostsAmount, setSalaryExtraCostsAmount] = useState(0);
  const [totalSalaryBurden, setTotalSalaryBurden] = useState(0);
  const [salaryHourRate, setSalaryHourRate] = useState(0);
  const [salaryDayRate, setSalaryDayRate] = useState(0);
  const [salaryWeekRate, setSalaryWeekRate] = useState(0);

  // Add search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filter employees based on search query
  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Load labor burden data on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!effectiveUid) return;

      try {
        setLoading(true);
        const laborBurdenData = await loadLaborBurdenData(effectiveUid);
        
        // Set employees
        setEmployees(laborBurdenData.employees || []);
        
        // Set global inputs
        setWorkDays(laborBurdenData.workDays || 251);
        setPto(laborBurdenData.pto || 10);
        setHolidays(laborBurdenData.holidays || 7);
        
        // Set hourly employee inputs
        setHourlyRate(laborBurdenData.hourlyRate || 30);
        setHourlyExtraCosts(laborBurdenData.hourlyExtraCosts || 15);
        setUnbillablePercentage(laborBurdenData.unbillablePercentage || 10);
        
        // Set salaried employee inputs
        setSalaryType(laborBurdenData.salaryType || 'annual');
        setSalaryRate(laborBurdenData.salaryRate || 62400);
        setSalaryExtraCosts(laborBurdenData.salaryExtraCosts || 15);
        
      } catch (error) {
        console.error('Error loading labor burden data:', error);
        setMessage({ type: 'error', text: 'Failed to load labor burden data' });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [effectiveUid]);

  // Auto-save function
  const autoSave = async () => {
    if (!effectiveUid || loading) return;

    try {
      setSaving(true);
      await saveLaborBurdenData({
        employees,
        workDays,
        pto,
        holidays,
        hourlyRate,
        hourlyExtraCosts,
        unbillablePercentage,
        salaryType,
        salaryRate,
        salaryExtraCosts
      }, effectiveUid);
    } catch (error) {
      console.error('Error auto-saving:', error);
      setMessage({ type: 'error', text: 'Failed to save data' });
    } finally {
      setSaving(false);
    }
  };

  // Manual save function
  const handleManualSave = async () => {
    if (!effectiveUid || loading) return;

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await saveLaborBurdenData({
        employees,
        workDays,
        pto,
        holidays,
        hourlyRate,
        hourlyExtraCosts,
        unbillablePercentage,
        salaryType,
        salaryRate,
        salaryExtraCosts
      }, effectiveUid);
    } catch (error) {
      console.error('Error saving:', error);
      setMessage({ type: 'error', text: 'Failed to save data' });
    } finally {
      setSaving(false);
    }
  };

  // Generate from org chart function
  const handleGenerateFromOrgChart = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const orgEmployees = await generateFromOrgChart(effectiveUid);
      setEmployees(orgEmployees);

      // Auto-save the generated data
      await saveLaborBurdenData({
        employees: orgEmployees,
        workDays,
        pto,
        holidays,
        hourlyRate,
        hourlyExtraCosts,
        unbillablePercentage,
        salaryType,
        salaryRate,
        salaryExtraCosts
      }, effectiveUid);
      

    } catch (error) {
      console.error('Error generating from org chart:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to generate from org chart' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Calculate hourly employee burden
  useEffect(() => {
    // Extra costs calculation
    const extraCosts = hourlyRate * (hourlyExtraCosts / 100);
    setExtraCostsAmount(extraCosts);
    
    // Hourly burden
    const burden = hourlyRate + extraCosts;
    setHourlyBurden(burden);
    
    // Unbillable calculation
    const unbillable = (burden / (1 - unbillablePercentage / 100)) - burden;
    setUnbillableAmount(unbillable);
    
    // Days off calculation
    const daysOff = (burden / (1 - (pto / workDays))) - burden;
    setDaysOffAmount(daysOff);
    
    // Holidays calculation
    const holidayAmount = (burden / (1 - (holidays / workDays))) - burden;
    setHolidaysAmount(holidayAmount);
    
    // Total hourly burden
    const totalBurden = burden + unbillable + daysOff + holidayAmount;
    setTotalHourlyBurden(totalBurden);
    
    // Daily, weekly, monthly, annual rates
    setHourlyDayRate(totalBurden * 8);
    setHourlyWeekRate(totalBurden * 40);
    setHourlyMonthRate((totalBurden * 40 * 52) / 12);
    setHourlyAnnualRate(totalBurden * 40 * 52);
  }, [hourlyRate, hourlyExtraCosts, unbillablePercentage, pto, holidays, workDays]);

  // Auto-save when employees change
  useEffect(() => {
    if (didLoadRef.current && !loading && effectiveUid && employees.length > 0) {
      const timeoutId = setTimeout(() => {
        autoSave();
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(timeoutId);
    }
  }, [employees]);

  // Auto-save when global inputs change
  useEffect(() => {
    if (didLoadRef.current && !loading && effectiveUid) {
      const timeoutId = setTimeout(() => {
        autoSave();
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(timeoutId);
    }
  }, [workDays, pto, holidays]);

  // Auto-save when hourly inputs change
  useEffect(() => {
    if (didLoadRef.current && !loading && effectiveUid) {
      const timeoutId = setTimeout(() => {
        autoSave();
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(timeoutId);
    }
  }, [hourlyRate, hourlyExtraCosts, unbillablePercentage]);

  // Auto-save when salary inputs change
  useEffect(() => {
    if (didLoadRef.current && !loading && effectiveUid) {
      const timeoutId = setTimeout(() => {
        autoSave();
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(timeoutId);
    }
  }, [salaryType, salaryRate, salaryExtraCosts]);

  // Enable autosave only once the initial load has completed. Defined AFTER the
  // autosave effects so, on the post-load commit, those effects skip the
  // load-triggered save (didLoadRef still false) before this flips it true.
  useEffect(() => {
    if (!loading && effectiveUid) didLoadRef.current = true;
  }, [loading, effectiveUid]);

  // Calculate salaried employee burden
  useEffect(() => {
    let weekly, monthly, annual;
    
    // Convert salary to all timeframes based on input type
    if (salaryType === 'weekly') {
      weekly = salaryRate;
      monthly = weekly * 52 / 12;
      annual = weekly * 52;
    } else if (salaryType === 'monthly') {
      monthly = salaryRate;
      annual = monthly * 12;
      weekly = annual / 52;
    } else { // annual
      annual = salaryRate;
      monthly = annual / 12;
      weekly = annual / 52;
    }
    
    setWeeklyRate(weekly);
    setMonthlyRate(monthly);
    setAnnualRate(annual);
    
    // Calculate extra costs
    const extraCosts = {
      weekly: weekly * (salaryExtraCosts / 100),
      monthly: monthly * (salaryExtraCosts / 100),
      annual: annual * (salaryExtraCosts / 100)
    };
    setSalaryExtraCostsAmount(extraCosts);
    
    // Calculate total burden
    const totalBurden = {
      weekly: weekly + extraCosts.weekly,
      monthly: monthly + extraCosts.monthly,
      annual: annual + extraCosts.annual
    };
    setTotalSalaryBurden(totalBurden);
    
    // Calculate hourly rate (based on 40-hour workweek)
    setSalaryHourRate(totalBurden.annual / (workDays * 8));
    
    // Calculate daily rate
    setSalaryDayRate(totalBurden.annual / workDays);
    
    // Weekly rate is already calculated
    setSalaryWeekRate(totalBurden.weekly);
  }, [salaryRate, salaryType, salaryExtraCosts, workDays]);

  // Calculate summary statistics for My Company tab
  useEffect(() => {
    if (employees.length === 0) {
      setSummaryStats({
        hourly: 0,
        daily: 0,
        weekly: 0,
        monthly: 0,
        annual: 0
      });
      return;
    }

    // Calculate total annual cost for all employees
    const totalAnnual = employees.reduce((sum, emp) => {
      const costs = calculateEmployeeCosts(emp);
      return sum + costs.annual;
    }, 0);

    // Calculate hourly rate based on total annual cost
    const totalHourly = totalAnnual / (workDays * 8);

    setSummaryStats({
      hourly: totalHourly,
      daily: totalHourly * 8,
      weekly: totalAnnual / 52,
      monthly: totalAnnual / 12,
      annual: totalAnnual
    });
  }, [employees, pto, holidays, workDays]);

  // Handle adding new employee
  const handleAddEmployee = async () => {
    if (!newEmployee.name || !newEmployee.jobTitle) {
      alert('Please fill in both name and job title');
      return;
    }
    
    try {
      setSaving(true);
      const newEmp = await addLaborBurdenEmployee({
        ...newEmployee,
        extraCosts: 15,
        unbillablePercentage: newEmployee.type === 'hourly' ? 10 : 0
      }, effectiveUid);
      setEmployees([...employees, newEmp]);
      setShowAddModal(false);
      setNewEmployee({
        type: 'hourly',
        name: '',
        jobTitle: '',
        rate: 0
      });
    } catch (error) {
      console.error('Error adding employee:', error);
      setMessage({ type: 'error', text: 'Failed to add employee' });
    } finally {
      setSaving(false);
    }
  };

  // Handle updating employee
  const handleUpdateEmployee = async () => {
    if (!editingEmployee.name || !editingEmployee.jobTitle) {
      alert('Please fill in both name and job title');
      return;
    }
    
    try {
      setSaving(true);
      await updateLaborBurdenEmployee(editingEmployee.id, editingEmployee, effectiveUid);
      setEmployees(employees.map(emp => 
        emp.id === editingEmployee.id ? editingEmployee : emp
      ));
      setShowEditModal(false);
      setEditingEmployee(null);
    } catch (error) {
      console.error('Error updating employee:', error);
      setMessage({ type: 'error', text: 'Failed to update employee' });
    } finally {
      setSaving(false);
    }
  };

  // Handle removing employee
  const handleRemoveEmployee = async (id) => {
    try {
      setSaving(true);
      await removeLaborBurdenEmployee(id, effectiveUid);
      setEmployees(employees.filter(emp => emp.id !== id));
      setShowEditModal(false);
      setEditingEmployee(null);
    } catch (error) {
      console.error('Error removing employee:', error);
      setMessage({ type: 'error', text: 'Failed to remove employee' });
    } finally {
      setSaving(false);
    }
  };

  // Format currency with decimals for hourly and daily rates
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format currency without decimals for weekly, monthly, and annual values
  const formatCurrencyNoDecimals = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Calculate employee costs
  const calculateEmployeeCosts = (employee) => {
    if (employee.type === 'hourly') {
      const burden = employee.rate * (1 + employee.extraCosts / 100);
      const unbillable = (burden / (1 - employee.unbillablePercentage / 100)) - burden;
      const daysOff = (burden / (1 - (pto / workDays))) - burden;
      const holidayAmount = (burden / (1 - (holidays / workDays))) - burden;
      const totalHourly = burden + unbillable + daysOff + holidayAmount;
      
      return {
        weekly: totalHourly * 40,
        monthly: (totalHourly * 40 * 52) / 12,
        annual: totalHourly * 40 * 52
      };
    } else {
      const annualRate = employee.rate * (1 + employee.extraCosts / 100);
      return {
        weekly: annualRate / 52,
        monthly: annualRate / 12,
        annual: annualRate
      };
    }
  };

  return (
    <div className={`${embedded ? '' : 'relative p-6 max-w-6xl mx-auto min-h-screen'} text-gray-900 dark:text-white`}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? '' : 'relative'}>
      {!embedded && <FinancePageHeader title="Labor Burden Calculator" />}

      {/* Message Display */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* My Company */}
      <div>
          {/* Summary Statistics */}
          <div className="mb-8">
            <div className={`flex items-center justify-between mb-4 border-b pb-2 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
              <div className="w-1/3"></div>
              <h2 className={`text-xl font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-700'}`}>Company Summary</h2>
              <div className="w-1/3 flex justify-end">
                <button
                  onClick={handleGenerateFromOrgChart}
                  disabled={saving}
                  title="Rebuild the employee list from your org chart"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                    saving
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                      : darkMode
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  <FaDownload className="w-3 h-3" />
                  {saving ? 'Generating...' : 'Generate from Org Chart'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <div className={`${darkMode ? 'bg-gradient-to-b from-red-900/30 to-gray-700 border-red-700' : 'bg-gradient-to-b from-red-50 to-white border-red-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Per Hour</div>
                <div className={`font-bold text-lg ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{formatCurrency(summaryStats.hourly)}</div>
              </div>
              <div className={`${darkMode ? 'bg-gradient-to-b from-red-900/30 to-gray-700 border-red-700' : 'bg-gradient-to-b from-red-50 to-white border-red-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Per Day</div>
                <div className={`font-bold text-lg ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{formatCurrency(summaryStats.daily)}</div>
              </div>
              <div className={`${darkMode ? 'bg-gradient-to-b from-red-900/30 to-gray-700 border-red-700' : 'bg-gradient-to-b from-red-50 to-white border-red-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Weekly</div>
                <div className={`font-bold text-lg ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{formatCurrencyNoDecimals(summaryStats.weekly)}</div>
              </div>
              <div className={`${darkMode ? 'bg-gradient-to-b from-red-900/30 to-gray-700 border-red-700' : 'bg-gradient-to-b from-red-50 to-white border-red-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Monthly</div>
                <div className={`font-bold text-lg ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{formatCurrencyNoDecimals(summaryStats.monthly)}</div>
              </div>
              <div className={`${darkMode ? 'bg-gradient-to-b from-red-900/30 to-gray-700 border-red-700' : 'bg-gradient-to-b from-red-50 to-white border-red-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Annual</div>
                <div className={`font-bold text-lg ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{formatCurrencyNoDecimals(summaryStats.annual)}</div>
              </div>
            </div>
          </div>

          {/* Add Employee Button and Search Bar */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex-1 mr-4">
              <input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-red-200 focus:border-red-400 transition-colors ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-700 placeholder-gray-500'
                }`}
              />
            </div>
            <AddButton onClick={() => setShowAddModal(true)} />
          </div>

          {/* Employee Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEmployees.map((employee) => {
              const costs = calculateEmployeeCosts(employee);
              return (
                <div
                  key={employee.id}
                  className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4 cursor-pointer transition-all hover:shadow-md flex flex-col items-center text-center`}
                  onClick={() => {
                    setEditingEmployee({ ...employee });
                    setShowEditModal(true);
                  }}
                >
                  <span className={`px-2 py-1 text-xs rounded-full mb-2 ${
                    employee.type === 'hourly'
                      ? darkMode ? 'bg-indigo-900 text-indigo-200' : 'bg-indigo-100 text-indigo-800'
                      : darkMode ? 'bg-teal-900 text-teal-200' : 'bg-teal-100 text-teal-800'
                  }`}>
                    {employee.type === 'hourly' ? 'Hourly' : 'Salaried'}
                  </span>
                  <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-700'}`}>{employee.name}</h3>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Annual Cost:</span>
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-700'}`}>{formatCurrencyNoDecimals(costs.annual)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add Employee Modal */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-md`}>
                <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Add New Employee</h3>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Name</label>
                    <input
                      type="text"
                      value={newEmployee.name}
                      onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                      className={`w-full p-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                      placeholder="Employee Name"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Job Title</label>
                    <input
                      type="text"
                      value={newEmployee.jobTitle}
                      onChange={(e) => setNewEmployee({ ...newEmployee, jobTitle: e.target.value })}
                      className={`w-full p-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                      placeholder="Job Title"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Employee Type</label>
                    <select
                      value={newEmployee.type}
                      onChange={(e) => setNewEmployee({ ...newEmployee, type: e.target.value })}
                      className={`w-full p-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    >
                      <option value="hourly">Hourly</option>
                      <option value="salaried">Salaried</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Rate ({newEmployee.type === 'hourly' ? 'Hourly' : 'Annual'})
                    </label>
                    <input
                      type="number"
                      value={newEmployee.rate}
                      onChange={(e) => setNewEmployee({ ...newEmployee, rate: Number(e.target.value) })}
                      className={`w-full p-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>

                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className={`px-4 py-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-700 hover:text-gray-900'}`}
                  >
                    Cancel
                  </button>
                  <AddButton onClick={handleAddEmployee} />
                </div>
              </div>
            </div>
          )}

          {/* Edit Employee Modal */}
          {showEditModal && editingEmployee && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-md`}>
                <div className="text-center mb-4">
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Edit Employee</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Name</label>
                    <input
                      type="text"
                      value={editingEmployee.name}
                      onChange={(e) => setEditingEmployee({ ...editingEmployee, name: e.target.value })}
                      className={`w-full p-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Job Title</label>
                    <input
                      type="text"
                      value={editingEmployee.jobTitle}
                      onChange={(e) => setEditingEmployee({ ...editingEmployee, jobTitle: e.target.value })}
                      className={`w-full p-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Employee Type</label>
                    <select
                      value={editingEmployee.type}
                      onChange={(e) => setEditingEmployee({ ...editingEmployee, type: e.target.value })}
                      className={`w-full p-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    >
                      <option value="hourly">Hourly</option>
                      <option value="salaried">Salaried</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Rate ({editingEmployee.type === 'hourly' ? 'Hourly' : 'Annual'})
                    </label>
                    <input
                      type="number"
                      value={editingEmployee.rate}
                      onChange={(e) => setEditingEmployee({ ...editingEmployee, rate: Number(e.target.value) })}
                      className={`w-full p-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Extra Costs (%)</label>
                    <input
                      type="number"
                      value={editingEmployee.extraCosts}
                      onChange={(e) => setEditingEmployee({ ...editingEmployee, extraCosts: Number(e.target.value) })}
                      className={`w-full p-2 border rounded-md ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                  {editingEmployee.type === 'hourly' && (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Unbillable (%)</label>
                      <input
                        type="number"
                        value={editingEmployee.unbillablePercentage}
                        onChange={(e) => setEditingEmployee({ ...editingEmployee, unbillablePercentage: Number(e.target.value) })}
                        className={`w-full p-2 border rounded-md ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-gray-200' 
                            : 'bg-white border-gray-300 text-gray-700'
                        }`}
                      />
                    </div>
                  )}
                </div>
                <div className="mt-6 flex justify-between">
                  <button
                    onClick={() => handleRemoveEmployee(editingEmployee.id)}
                    className="px-4 py-2 text-red-600 hover:text-red-800"
                  >
                    Delete Employee
                  </button>
                  <div className="space-x-3">
                    <button
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingEmployee(null);
                      }}
                      className={`px-4 py-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-700 hover:text-gray-900'}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateEmployee}
                      className={`px-4 py-2 rounded-md font-medium transition-colors ${
                        darkMode
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-red-500 hover:bg-red-600 text-white'
                      }`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Comparison */}
      <div className="mt-12">
          <h2 className={`text-xl font-semibold text-center border-b pb-2 mb-6 ${darkMode ? 'text-white border-gray-600' : 'text-gray-700 border-gray-200'}`}>Comparison</h2>

          {/* Global Inputs */}
          <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 mb-8`}>
            <div className="flex items-center justify-between mb-4 border-b pb-2">
              <div className="w-1/3"></div>
              <h2 className={`text-xl font-semibold text-center ${darkMode ? 'text-white border-gray-600' : 'text-gray-700 border-gray-200'}`}>Time Off & Workdays</h2>
              <div className="w-1/3 flex justify-end">
                <SaveButton onClick={handleManualSave} disabled={saving} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Work Days Per Year</label>
                <input
                  type="number"
                  value={workDays}
                  onChange={(e) => setWorkDays(Number(e.target.value))}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200' 
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>PTO Days</label>
                <input
                  type="number"
                  value={pto}
                  onChange={(e) => setPto(Number(e.target.value))}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200' 
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Holidays</label>
                <input
                  type="number"
                  value={holidays}
                  onChange={(e) => setHolidays(Number(e.target.value))}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200' 
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Hourly Employee Section */}
            <div>
              {/* Hourly Employee Inputs */}
              <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 mb-6`}>
                <div className="flex items-center justify-between mb-4 border-b pb-2">
                  <div className="w-1/3"></div>
                  <h2 className={`text-xl font-semibold text-center ${darkMode ? 'text-indigo-300 border-gray-600' : 'text-indigo-700 border-gray-200'}`}>Hourly Employee</h2>
                  <div className="w-1/3 flex justify-end">
                    <SaveButton onClick={handleManualSave} disabled={saving} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Hourly Rate ($)</label>
                    <input
                      type="number"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(Number(e.target.value))}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Extra Costs (%)
                      <FaInfoCircle 
                        className={`ml-2 cursor-help ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                        title="Additional employer costs beyond base salary/wage including: Payroll taxes (FICA, FUTA, SUTA), Health insurance premiums, Retirement contributions (401k, pension), Workers' compensation insurance, Professional development, Equipment/software, Office space, and other fringe benefits. Typically ranges from 15-40% of base compensation."
                      />
                    </label>
                    <input
                      type="number"
                      value={hourlyExtraCosts}
                      onChange={(e) => setHourlyExtraCosts(Number(e.target.value))}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Marked Unbillable (%)</label>
                    <input
                      type="number"
                      value={unbillablePercentage}
                      onChange={(e) => setUnbillablePercentage(Number(e.target.value))}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                </div>
              </div>
              
              {/* Hourly Total Labor Load */}
              <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5`}>
                <h3 className={`text-lg font-semibold mb-4 border-b pb-2 text-center ${darkMode ? 'text-indigo-300 border-gray-600' : 'text-indigo-700 border-gray-200'}`}>Total Labor Load</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <div className={`${darkMode ? 'bg-gradient-to-b from-indigo-900/30 to-gray-700 border-indigo-700' : 'bg-gradient-to-b from-indigo-50 to-white border-indigo-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                    <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Hour</div>
                    <div className={`font-bold text-lg ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{formatCurrency(totalHourlyBurden)}</div>
                  </div>
                  <div className={`${darkMode ? 'bg-gradient-to-b from-indigo-900/30 to-gray-700 border-indigo-700' : 'bg-gradient-to-b from-indigo-50 to-white border-indigo-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                    <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Day</div>
                    <div className={`font-bold text-lg ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{formatCurrency(hourlyDayRate)}</div>
                  </div>
                  <div className={`${darkMode ? 'bg-gradient-to-b from-indigo-900/30 to-gray-700 border-indigo-700' : 'bg-gradient-to-b from-indigo-50 to-white border-indigo-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                    <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Weekly</div>
                    <div className={`font-bold text-lg ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{formatCurrencyNoDecimals(hourlyWeekRate)}</div>
                  </div>
                  <div className={`${darkMode ? 'bg-gradient-to-b from-indigo-900/30 to-gray-700 border-indigo-700' : 'bg-gradient-to-b from-indigo-50 to-white border-indigo-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                    <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Monthly</div>
                    <div className={`font-bold text-lg ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{formatCurrencyNoDecimals(hourlyMonthRate)}</div>
                  </div>
                  <div className={`${darkMode ? 'bg-gradient-to-b from-indigo-900/30 to-gray-700 border-indigo-700' : 'bg-gradient-to-b from-indigo-50 to-white border-indigo-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                    <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Annual</div>
                    <div className={`font-bold text-lg ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{formatCurrencyNoDecimals(hourlyAnnualRate)}</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Salaried Employee Section */}
            <div>
              {/* Salaried Employee Inputs */}
              <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 mb-6`}>
                <div className="flex items-center justify-between mb-4 border-b pb-2">
                  <div className="flex-1"></div>
                  <h2 className={`text-xl font-semibold text-center whitespace-nowrap px-2 ${darkMode ? 'text-teal-300 border-gray-600' : 'text-teal-700 border-gray-200'}`}>Salaried Employee</h2>
                  <div className="flex-1 flex justify-end">
                    <SaveButton onClick={handleManualSave} disabled={saving} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Salary Input Type</label>
                    <select
                      value={salaryType}
                      onChange={(e) => setSalaryType(e.target.value)}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-200 focus:border-teal-400 transition-colors ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Salary Rate ({salaryType === 'weekly' ? 'Weekly' : salaryType === 'monthly' ? 'Monthly' : 'Annual'})
                    </label>
                    <input
                      type="number"
                      value={salaryRate}
                      onChange={(e) => setSalaryRate(Number(e.target.value))}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-200 focus:border-teal-400 transition-colors ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Extra Costs (%)
                      <FaInfoCircle 
                        className={`ml-2 cursor-help ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                        title="Additional employer costs beyond base salary/wage including: Payroll taxes (FICA, FUTA, SUTA), Health insurance premiums, Retirement contributions (401k, pension), Workers' compensation insurance, Professional development, Equipment/software, Office space, and other fringe benefits. Typically ranges from 15-40% of base compensation."
                      />
                    </label>
                    <input
                      type="number"
                      value={salaryExtraCosts}
                      onChange={(e) => setSalaryExtraCosts(Number(e.target.value))}
                      className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-200 focus:border-teal-400 transition-colors ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                </div>
              </div>
              
              {/* Salary Total Labor Load */}
              <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5`}>
                <h3 className={`text-lg font-semibold mb-4 border-b pb-2 text-center ${darkMode ? 'text-teal-300 border-gray-600' : 'text-teal-700 border-gray-200'}`}>Total Labor Load</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <div className={`${darkMode ? 'bg-gradient-to-b from-teal-900/30 to-gray-700 border-teal-700' : 'bg-gradient-to-b from-teal-50 to-white border-teal-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                    <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Hour</div>
                    <div className={`font-bold text-lg ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>{formatCurrency(salaryHourRate)}</div>
                  </div>
                  <div className={`${darkMode ? 'bg-gradient-to-b from-teal-900/30 to-gray-700 border-teal-700' : 'bg-gradient-to-b from-teal-50 to-white border-teal-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                    <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Day</div>
                    <div className={`font-bold text-lg ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>{formatCurrency(salaryDayRate)}</div>
                  </div>
                  <div className={`${darkMode ? 'bg-gradient-to-b from-teal-900/30 to-gray-700 border-teal-700' : 'bg-gradient-to-b from-teal-50 to-white border-teal-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                    <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Weekly</div>
                    <div className={`font-bold text-lg ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>{formatCurrencyNoDecimals(salaryWeekRate)}</div>
                  </div>
                  <div className={`${darkMode ? 'bg-gradient-to-b from-teal-900/30 to-gray-700 border-teal-700' : 'bg-gradient-to-b from-teal-50 to-white border-teal-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                    <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Monthly</div>
                    <div className={`font-bold text-lg ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>{formatCurrencyNoDecimals(totalSalaryBurden.monthly)}</div>
                  </div>
                  <div className={`${darkMode ? 'bg-gradient-to-b from-teal-900/30 to-gray-700 border-teal-700' : 'bg-gradient-to-b from-teal-50 to-white border-teal-100'} border rounded-lg p-3 flex flex-col items-center shadow-sm`}>
                    <div className={`text-xs mb-1 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Annual</div>
                    <div className={`font-bold text-lg ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>{formatCurrencyNoDecimals(totalSalaryBurden.annual)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>
      </div>
    </div>
  );
};

export default LaborBurdenCalculator; 